import { Logic } from '~common/ui-logic/logic'
import { executeTask } from '~common/ui-logic/tasks'
import type { TaskState } from '~/services/cache/types'
import type { AppContextType } from '~/app-context'
import { FirebaseError } from 'firebase/app'

export interface LoginLogicDependencies {}

export interface LoginLogicState {
    mode: 'login' | 'register'
    email: string
    password: string
    confirmPassword: string
    error: string | null
    loadState: TaskState
    popupBlocked: boolean
}

export class LoginLogic extends Logic<
    LoginLogicDependencies & AppContextType,
    LoginLogicState
> {
    getInitialState = (): LoginLogicState => ({
        mode: 'login',
        email: '',
        password: '',
        confirmPassword: '',
        error: null,
        loadState: 'pristine',
        popupBlocked: false,
    })

    async initialize() {
        const currentUser = await this.deps.services.auth.getLoggedInUser()
        if (currentUser) {
            window.location.href = '/'
        }
    }

    private checkPopupBlocked(): boolean {
        try {
            const testPopup = window.open(
                '',
                '_blank',
                'width=1,height=1,top=0,left=0',
            )
            if (
                !testPopup ||
                testPopup.closed ||
                typeof testPopup.closed === 'undefined'
            ) {
                return true
            }
            testPopup.close()
            return false
        } catch {
            return true
        }
    }

    setMode = (mode: 'login' | 'register') => {
        this.setState({
            ...this.state,
            mode,
            error: null,
            password: '',
            confirmPassword: '',
        })
    }

    setEmail = (email: string) => {
        this.setState({
            ...this.state,
            email,
            error: null,
        })
    }

    setPassword = (password: string) => {
        this.setState({
            ...this.state,
            password,
            error: null,
        })
    }

    setConfirmPassword = (confirmPassword: string) => {
        this.setState({
            ...this.state,
            confirmPassword,
            error: null,
        })
    }

    handleEmailLogin = async () => {
        if (!this.state.email || !this.state.password) {
            this.setState({
                ...this.state,
                error: 'Please enter both email and password',
            })
            return
        }

        await executeTask(this, 'loadState', async () => {
            try {
                const result = await this.deps.services.auth.loginWithEmail(
                    this.state.email,
                    this.state.password,
                )

                if (!result.success) {
                    this.setState({
                        ...this.state,
                        error: result.error,
                        loadState: 'pristine',
                    })
                    return
                }

                await this.handleLoginSuccess(false)
            } catch (error) {
                this.setState({
                    ...this.state,
                    error: this.getErrorMessage(error),
                    loadState: 'pristine',
                })
            }
        })
    }

    handleEmailRegister = async () => {
        if (!this.state.email || !this.state.password) {
            this.setState({
                ...this.state,
                error: 'Please enter both email and password',
            })
            return
        }

        if (this.state.password !== this.state.confirmPassword) {
            this.setState({
                ...this.state,
                error: 'Passwords do not match',
            })
            return
        }

        if (this.state.password.length < 6) {
            this.setState({
                ...this.state,
                error: 'Password must be at least 6 characters',
            })
            return
        }

        await executeTask(this, 'loadState', async () => {
            try {
                const result = await this.deps.services.auth.signUpWithEmail(
                    this.state.email,
                    this.state.password,
                )

                if (!result.success) {
                    this.setState({
                        ...this.state,
                        error: result.error,
                        loadState: 'pristine',
                    })
                    return
                }

                await this.handleLoginSuccess(true)
            } catch (error) {
                this.setState({
                    ...this.state,
                    error: this.getErrorMessage(error),
                    loadState: 'pristine',
                })
            }
        })
    }

    handleGoogleAuth = async () => {
        const blocked = this.checkPopupBlocked()
        if (blocked) {
            this.setState({
                ...this.state,
                popupBlocked: true,
            })
            return
        }

        await executeTask(this, 'loadState', async () => {
            try {
                const result = await this.deps.services.auth.loginWithGoogle()

                if (!result) {
                    throw new Error('Failed to login with Google')
                }

                const currentUser =
                    await this.deps.services.auth.getLoggedInUser()
                if (currentUser?.email) {
                    this.deps.services.googleApi?.setActiveEmail(
                        currentUser.email,
                    )
                    this.deps.services.googleApi?.setToken(
                        currentUser.email,
                        result.accessToken,
                    )
                }
                this.deps.services.googleApi?.initializePrimary(
                    result.accessToken,
                )

                await this.handleLoginSuccess(result.newUser)
            } catch (error) {
                let errorMessage: string
                if (
                    error instanceof Error &&
                    error.message.includes('popup_closed_by_user')
                ) {
                    errorMessage = ''
                } else {
                    errorMessage =
                        error instanceof Error
                            ? error.message
                            : 'Google sign-in failed. Please try again.'
                }
                this.setState({
                    ...this.state,
                    loadState: 'pristine',
                    error: errorMessage,
                })
            }
        })
    }

    private async handleLoginSuccess(newUser: boolean) {
        window.location.href = '/'
    }

    recheckPopupBlocked = () => {
        const blocked = this.checkPopupBlocked()
        this.setState({
            ...this.state,
            popupBlocked: blocked,
        })
    }

    private getErrorMessage(error: unknown): string {
        if (error instanceof FirebaseError) {
            switch (error.code) {
                case 'auth/network-request-failed':
                    return 'Network error. Please check your internet connection and try again.'
                case 'auth/invalid-email':
                    return 'Invalid email address.'
                case 'auth/user-disabled':
                    return 'This account has been disabled.'
                case 'auth/user-not-found':
                    return 'No account found with this email address.'
                case 'auth/wrong-password':
                    return 'Incorrect password.'
                case 'auth/email-already-in-use':
                    return 'An account with this email already exists.'
                case 'auth/weak-password':
                    return 'Password is too weak. Please use a stronger password.'
                case 'auth/too-many-requests':
                    return 'Too many failed attempts. Please try again later.'
                default:
                    return (
                        error.message || 'An error occurred. Please try again.'
                    )
            }
        }
        if (error instanceof Error) {
            return error.message
        }
        return 'An unexpected error occurred. Please try again.'
    }
}
